function normalizeIdentifier(value) {
  if (typeof value === "string") {
    return value.trim() === "" ? null : value;
  }

  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function normalizeEconomicValue(value) {
  if (
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "") ||
    (typeof value !== "number" && typeof value !== "string")
  ) {
    return null;
  }

  const number = Number(value);

  return Number.isFinite(number) ? Math.max(number, 0) : null;
}

function normalizeAlternative(alternative) {
  const source =
    alternative !== null && typeof alternative === "object" ? alternative : {};
  const id = normalizeIdentifier(source.id);
  const totalRiskCost = normalizeEconomicValue(source.totalRiskCost);
  const residualExpectedLoss = normalizeEconomicValue(
    source.residualExpectedLoss
  );
  const isEligible =
    id !== null && totalRiskCost !== null && residualExpectedLoss !== null;

  return {
    source,
    id,
    totalRiskCost,
    residualExpectedLoss,
    isEligible,
  };
}

export function analyzeDecisionFrontier(alternatives = []) {
  const input = Array.isArray(alternatives) ? alternatives : [];
  const normalized = input.map(normalizeAlternative);

  const evaluated = normalized.map((alternative, index) => {
    const dominatedBy = alternative.isEligible
      ? normalized
          .filter(
            (candidate, candidateIndex) =>
              candidateIndex !== index &&
              candidate.isEligible &&
              candidate.totalRiskCost <= alternative.totalRiskCost &&
              candidate.residualExpectedLoss <=
                alternative.residualExpectedLoss &&
              (candidate.totalRiskCost < alternative.totalRiskCost ||
                candidate.residualExpectedLoss <
                  alternative.residualExpectedLoss)
          )
          .map((candidate) => candidate.id)
      : [];
    const isDominated = dominatedBy.length > 0;

    return {
      ...alternative.source,
      ...(alternative.isEligible
        ? {
            totalRiskCost: alternative.totalRiskCost,
            residualExpectedLoss: alternative.residualExpectedLoss,
          }
        : {}),
      frontier: {
        isEligible: alternative.isEligible,
        isEfficient: alternative.isEligible && !isDominated,
        isDominated,
        dominatedBy,
        exclusionReason: alternative.isEligible ? null : "INVALID_ALTERNATIVE",
      },
    };
  });

  const eligibleIndexes = normalized
    .map((alternative, index) => (alternative.isEligible ? index : -1))
    .filter((index) => index !== -1);
  const efficientAlternatives = evaluated.filter(
    (alternative) => alternative.frontier.isEfficient
  );
  const dominatedAlternatives = evaluated.filter(
    (alternative) => alternative.frontier.isDominated
  );
  const minimumCostIndex = eligibleIndexes.reduce(
    (minimum, index) =>
      minimum === null ||
      normalized[index].totalRiskCost < normalized[minimum].totalRiskCost
        ? index
        : minimum,
    null
  );
  const minimumRiskIndex = eligibleIndexes.reduce(
    (minimum, index) =>
      minimum === null ||
      normalized[index].residualExpectedLoss <
        normalized[minimum].residualExpectedLoss
        ? index
        : minimum,
    null
  );
  const referenceIndex = normalized.findIndex(
    (alternative) => alternative.source.isReference === true
  );

  return {
    alternatives: evaluated,
    efficientAlternatives,
    dominatedAlternatives,
    minimumCostAlternative:
      minimumCostIndex === null ? null : evaluated[minimumCostIndex],
    minimumRiskAlternative:
      minimumRiskIndex === null ? null : evaluated[minimumRiskIndex],
    referenceAlternative:
      referenceIndex === -1 ? null : evaluated[referenceIndex],
    metadata: {
      evaluatedAlternatives: input.length,
      eligibleAlternatives: eligibleIndexes.length,
      ineligibleAlternatives: input.length - eligibleIndexes.length,
      efficientAlternatives: efficientAlternatives.length,
      dominatedAlternatives: dominatedAlternatives.length,
    },
  };
}
